import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type {
  CampaignQuoteItemModel,
  CreateCampaignQuoteItemPayload,
  CreateMultipleCampaignQuoteItemsPayload,
} from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, ActionResult, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { updateCampaignQuoteKey } from '@redux/modules/campaignQuote'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignQuoteItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignQuoteItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignQuoteItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignQuoteItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignQuoteItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignQuoteItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/campaignQuoteItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/campaignQuoteItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/campaignQuoteItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/campaignQuoteItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/campaignQuoteItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/campaignQuoteItem/DELETE_FAILURE'

export type CampaignQuoteItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CampaignQuoteItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignQuoteItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignQuoteItems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign_quote_items.json', options)
      .then(({ data }: { data: CampaignQuoteItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignQuoteItem(campaignQuoteItemId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.campaignQuoteItems.loadedForKeys]
    const loadedIds = [...state.campaignQuoteItems.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (campaignQuoteItemId && !loadedIds.includes(campaignQuoteItemId)){
      loadedIds.push(campaignQuoteItemId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/campaign_quote_items/${campaignQuoteItemId}.json`, options)
      .then(({ data }: { data: CampaignQuoteItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteItem(
  itemPayload: CreateCampaignQuoteItemPayload,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_item: itemPayload,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quote_items.json', options, config)
      .then(({ data }: { data: CampaignQuoteItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        // Update Quote Data
        if (data.quote_total && data.campaign_quote_id){
          dispatch(updateCampaignQuoteKey({
            campaignQuoteId: data.campaign_quote_id,
            key: 'total',
            value: data.quote_total,
          }))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteItems(
  orderType: 'bundle' | 'publication',
  itemPayload: CreateMultipleCampaignQuoteItemsPayload,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_item: itemPayload,
      order_type: orderType,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quote_items/create_multiple.json', options, config)
      .then(({ data }: { data: CampaignQuoteItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        // Update Quote Data using first item in array
        if (data?.[0]?.campaign_quote_id && data?.[0]?.quote_total){
          dispatch(updateCampaignQuoteKey({
            campaignQuoteId: data[0].campaign_quote_id,
            key: 'total',
            value: data[0].quote_total,
          }))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteItem(
  campaignQuoteItem: UpdateParams<CampaignQuoteItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_item: campaignQuoteItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_items/${campaignQuoteItem.id}.json`, options, config)
      .then(({ data }: { data: CampaignQuoteItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        // Update Quote Data
        if (data.quote_total && data.campaign_quote_id){
          dispatch(updateCampaignQuoteKey({
            campaignQuoteId: data.campaign_quote_id,
            key: 'total',
            value: data.quote_total,
          }))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteItemOptions(
  campaignQuoteItemId: number,
  itemPayload: CreateCampaignQuoteItemPayload,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_item: itemPayload,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_items/${campaignQuoteItemId}/update_options.json`, options, config)
      .then(({ data }: { data: CampaignQuoteItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        // Update Quote Data
        if (data.quote_total && data.campaign_quote_id){
          dispatch(updateCampaignQuoteKey({
            campaignQuoteId: data.campaign_quote_id,
            key: 'total',
            value: data.quote_total,
          }))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateCampaignQuoteItem(
  campaignQuoteItem: UpdateParams<CampaignQuoteItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_items/${campaignQuoteItem.id}/duplicate.json`, options, config)
      .then(({ data }: { data: CampaignQuoteItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        // Update Quote Data
        if (data.quote_total && data.campaign_quote_id){
          dispatch(updateCampaignQuoteKey({
            campaignQuoteId: data.campaign_quote_id,
            key: 'quote_total',
            value: data.quote_total,
          }))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadDynamicCampaignQuoteItems(options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api('/campaign_quote_items/dynamic_items.json', options)
      .then(({ data }: { data: CampaignQuoteItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function bulkAssignAttributes(campaignQuoteItems: Partial<CampaignQuoteItemModel>[]) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_items: campaignQuoteItems,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/campaign_quote_items/bulk_update_attributes.json', {}, config)
      .then(({ data }: { data: CampaignQuoteItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteCampaignQuoteItem(campaignQuoteItem: DeleteParams<CampaignQuoteItemModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/campaign_quote_items/${campaignQuoteItem.id}.json`, {}, config)
      .then(({ data }: { data: {
        campaign_quote_id: number,
        campaign_quote_items: CampaignQuoteItemModel[],
        id: number,
        quote_total?: number,
      }, }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_ITEM)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        // Update Quote Data
        if (data.quote_total && campaignQuoteItem.campaign_quote_id){
          dispatch(updateCampaignQuoteKey({
            campaignQuoteId: campaignQuoteItem.campaign_quote_id,
            key: 'total',
            value: data.quote_total,
          }))
        }

        if (data.campaign_quote_id){
          dispatch(loadDynamicCampaignQuoteItems({ campaign_quote_id: data.campaign_quote_id }))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function artworkBuilderConfig(campaignQuoteItemId: number) {
  return (dispatch: AppDispatch): Promise<ActionResult<CampaignQuoteItemModel>> => {
    // Set Loading
    dispatch(fetchRequest())

    const promise = api(`/campaign_quote_items/${campaignQuoteItemId}/artwork_builder_config.json`)
      .then(({ data }: { data: object }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    // @ts-expect-error TS2322
    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignQuoteItemModuleState = initialState,
  action: Action = { type: '' },
): CampaignQuoteItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
